import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { imageApi, usageApi, xhsApi, galleryApi } from '../api/client'
import { BRAND_OPTIONS, PREMIUM_LIMITS, findBrandOption, getBrandBaseId, getDefaultBeadSize, gridToCm } from '../utils/beadData'
import { hexToRgb } from '../utils/color'
import {
  generateBeadPattern,
  remapPatternBrand,
  renderPatternToDataURL,
} from '../utils/imageProcessor'
import { exportPatternPng, exportPatternPdf, downloadDataUrl, downloadBlob } from '../utils/exportPattern'
import { IRONING_EFFECTS, renderIroningPreview } from '../utils/ironingRenderer'
import storage from '../utils/storage'
import BeadEditor from '../components/BeadEditor'
import TextBeadModal from '../components/TextBeadModal'
import ImageCropModal from '../components/ImageCropModal'

const CANVAS_SCALE = 16 // 每个格子渲染像素数
const MIN_CELL_FOR_CODE = 16 // 格子大于等于此尺寸才显示色号

// AI 生图等待时的小贴士（内置默认值；管理员可在后台自定义覆盖）
const AI_LOADING_TIPS = [
  // 熨烫
  '🔥 熨烫垫烘焙纸',
  '🔥 低温少量多次',
  '🔥 烫完压书定型',
  '🔥 大图正反各烫',
  '🔥 毛巾隔层起绒',
  // 色彩搭配
  '🎨 深色描边更清晰',
  '🎨 阴影用相邻色',
  '🎨 肤色选浅米系',
  '🎨 少色更干净',
  '🎨 撞色更出挑',
  // 网站功能
  '✨ 可预览熨烫效果',
  '✨ 吸附取色零色差',
  '✨ 图例按单买豆',
  '✨ 签到送 AI 额度',
  '✨ 分享码发好友',
  // 小鼻嘎与大图
  '🧩 小鼻嘎 29×29',
  '🧩 挂件留加固位',
  '🧩 大图先拼轮廓',
  '🧩 按 5×5 分区拼',
  '🧩 新手选 5mm 豆',
]

function dataURItoFile(dataURI, filename = 'image.png') {
  const byteString = atob(dataURI.split(',')[1])
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]
  const ab = new ArrayBuffer(byteString.length)
  const ia = new Uint8Array(ab)
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i)
  }
  return new File([ab], filename, { type: mimeString })
}

export default function Home() {
  const { user, isPremium, refreshUsage } = useAuth()
  const limits = isPremium ? PREMIUM_LIMITS.premium : PREMIUM_LIMITS.free
  // AI 生图：会员消耗额度；免费用户每日免费 1 张（服务端强制限制）
  const canUseAi = isPremium
    ? (user?.quota || 0) > 0 && (!user?.quotaExpiresAt || user.quotaExpiresAt > Date.now())
    : true

  // AI 额度过期日期展示
  const quotaExpiresText = useMemo(() => {
    const ts = user?.quotaExpiresAt
    if (!ts || ts <= 0) return ''
    const d = new Date(ts)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [user?.quotaExpiresAt])

  const [brandId, setBrandId] = useState(BRAND_OPTIONS[0].id)
  const [boardSize, setBoardSize] = useState(50)
  const [beadSize, setBeadSize] = useState(() => getDefaultBeadSize(getBrandBaseId(BRAND_OPTIONS[0].id)))
  const [patternTitle, setPatternTitle] = useState('')
  // 导出时分享选择：public 公开 / private 仅自己可见 / none 不保存
  const [saveMode, setSaveMode] = useState('public')
  // 导出镜像：默认非镜像，开启后 PNG/PDF 图纸水平翻转
  const [mirrorExport, setMirrorExport] = useState(false)
  const [image, setImage] = useState(null)
  // 图片来源：ai（高还原模式）/ upload / link，用于决定转换管线
  const [imageSource, setImageSource] = useState(null)
  // AI 像素图走「高还原」管线：主色采样、不描边、不合并/去杂色/抖动，最大化保留原图
  const faithful = imageSource === 'ai'
  const [pattern, setPattern] = useState(null) // { grid: [[colorId]], legend: {}, width, height, total }
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiStyle, setAiStyle] = useState('')
  const [aiTask, setAiTask] = useState(null) // { taskId, status, imageUrl }
  const [aiLoading, setAiLoading] = useState(false)
  // 生图等待小贴士轮播
  const [aiTipIndex, setAiTipIndex] = useState(0)
  // 小贴士内容：优先用管理员后台自定义的配置，未配置时用内置默认
  const [aiTips, setAiTips] = useState(AI_LOADING_TIPS)
  useEffect(() => {
    imageApi.tips()
      .then(({ data }) => {
        if (Array.isArray(data.tips) && data.tips.length > 0) setAiTips(data.tips)
      })
      .catch(() => {})
  }, [])
  // AI 参考图（风格，最多 1 张）与垫图（内容，最多 4 张）
  const [aiStyleRef, setAiStyleRef] = useState(null) // { id, src } | null
  const [aiBaseImages, setAiBaseImages] = useState([]) // [{ id, src }]
  const [aiUploading, setAiUploading] = useState(false)
  const [textModalOpen, setTextModalOpen] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  // 小红书导入
  const [xhsUrl, setXhsUrl] = useState('')
  const [xhsLoading, setXhsLoading] = useState(false)
  const [xhsImages, setXhsImages] = useState([])
  const [xhsTitle, setXhsTitle] = useState('')
  const [showXhsDisclaimer, setShowXhsDisclaimer] = useState(false)

  // 高级选项
  const [maxColors, setMaxColors] = useState(64)
  const [isCustomColorCount, setIsCustomColorCount] = useState(false)
  // 图像优化强度：0-100 无级调节，0/50/100 对应原 弱/标准/强 三档
  const [optimizeValue, setOptimizeValue] = useState(50)
  // 自动描边：默认关闭（只对主体外轮廓勾勒，需要时手动开启）
  const [outline, setOutline] = useState(false)
  // 抖动适合照片/渐变，默认关闭以避免图纸散点过多
  const [dithering, setDithering] = useState(false)
  // 普通图片自动优化：细节模式（五官不糊）+ 轻度增鲜 + 杂色清理
  // 注意：以下派生量依赖上面的 state，必须声明在 state 之后，否则会触发 TDZ 错误导致白屏
  // 分段线性映射合并阈值：0→0.03（弱）50→0.06（标准）100→0.10（强）
  const OPTIMIZE_LABEL = optimizeValue < 25 ? '弱 · 保留细节' : optimizeValue < 75 ? '标准 · 推荐' : '强 · 更干净'
  const effMerge = faithful
    ? 0
    : optimizeValue <= 50
      ? 0.03 + (optimizeValue / 50) * 0.03
      : 0.06 + ((optimizeValue - 50) / 50) * 0.04
  const effDenoise = faithful ? 0 : optimizeValue >= 75 ? 2 : 1
  const effPixelMode = faithful ? 'dominant' : 'detail'
  const effOutline = faithful ? false : outline
  const effDithering = faithful ? false : dithering
  const effSaturation = faithful ? 1 : 1.18
  const effPreprocessStrength = faithful ? 0 : optimizeValue / 100
  const [showGrid, setShowGrid] = useState(true)
  const [showGroupLines, setShowGroupLines] = useState(true)
  const [showColorCodes, setShowColorCodes] = useState(true)
  const [paramsLocked, setParamsLocked] = useState(false)
  // 锁定后由编辑器实时上报的画幅规格（随缩放变化）
  const [liveGridSize, setLiveGridSize] = useState(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isCellEditing, setIsCellEditing] = useState(false)
  const beadEditorRef = useRef(null)
  // 三步流程：1 选择原图 / 2 编辑调整 / 3 生成导出
  const [step, setStep] = useState(1)

  // === 编辑图纸模式 ===
  const [editMode, setEditMode] = useState(false)
  const [editedPattern, setEditedPattern] = useState(null)
  const [selectedColorId, setSelectedColorId] = useState(null)
  // 取色模式：点击图纸吸附该格颜色为当前画笔色，避免盲目补色造成色差
  const [pickColorMode, setPickColorMode] = useState(false)
  // 编辑历史（单步撤回）与草稿箱（自动保存）
  const [editHistory, setEditHistory] = useState([])
  const [draft, setDraft] = useState(null)
  // 会员水印
  const [userWatermark, setUserWatermark] = useState('')
  const [enableWatermark, setEnableWatermark] = useState(false)

  // 预览效果：pattern 图纸 / 熨烫效果（半熔、全熔、闪粉烫、毛巾烫…）
  const [previewEffect, setPreviewEffect] = useState('pattern')

  const previewCanvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const aiStyleRefInputRef = useRef(null)
  const aiBaseInputRef = useRef(null)
  const pollIntervalRef = useRef(null)

  // 草稿箱 key（按用户隔离，未登录用 guest）
  const draftKey = `pindou_draft_${user?.id || 'guest'}`

  // 加载草稿
  useEffect(() => {
    try {
      const raw = storage.get(draftKey)
      setDraft(raw ? JSON.parse(raw) : null)
    } catch {
      setDraft(null)
    }
  }, [draftKey])

  // 编辑时自动保存草稿（防抖 500ms）
  useEffect(() => {
    if (!editMode || !editedPattern) return
    const t = setTimeout(() => {
      const data = {
        pattern: editedPattern,
        brandId,
        beadSize,
        patternTitle,
        savedAt: Date.now(),
      }
      storage.set(draftKey, JSON.stringify(data))
      setDraft(data)
    }, 500)
    return () => clearTimeout(t)
  }, [editedPattern, editMode, brandId, beadSize, patternTitle, draftKey])

  // 依据 grid 重新计算色号图例与总豆数
  const recomputePatternStats = (grid) => {
    const h = grid.length
    const w = grid[0]?.length || 0
    const legend = new Map()
    let total = 0
    for (let ry = 0; ry < h; ry++) {
      for (let rx = 0; rx < w; rx++) {
        const id = grid[ry][rx]
        const color = brand.colors.find((c) => c.id === id)
        if (!color) continue
        const entry = legend.get(id) || { ...color, count: 0 }
        entry.count += 1
        legend.set(id, entry)
        total += 1
      }
    }
    return { legend: Array.from(legend.values()), total }
  }

  // 单步撤回：恢复上一版 grid
  const undoEdit = () => {
    if (!editHistory.length || !editedPattern) return
    const lastGrid = editHistory[editHistory.length - 1]
    const { legend, total } = recomputePatternStats(lastGrid)
    setEditedPattern({ ...editedPattern, grid: lastGrid, legend, total })
    setEditHistory((prev) => prev.slice(0, -1))
  }

  // 恢复草稿：载入图纸并直接进入编辑模式
  const restoreDraft = () => {
    if (!draft?.pattern) return
    setBrandId(draft.brandId || BRAND_OPTIONS[0].id)
    if (draft.beadSize) setBeadSize(draft.beadSize)
    if (draft.patternTitle) setPatternTitle(draft.patternTitle)
    setPattern(draft.pattern)
    setEditedPattern(structuredClone(draft.pattern))
    setSelectedColorId(null)
    setEditHistory([])
    setPickColorMode(false)
    setEditMode(true)
    setStep(3)
  }

  const discardDraft = () => {
    storage.remove(draftKey)
    setDraft(null)
  }

  const brand = findBrandOption(brandId)
  const availableBrands = useMemo(
    () => (isPremium ? BRAND_OPTIONS : BRAND_OPTIONS.filter((o) => o.baseId === 'mard')),
    [isPremium]
  )

  // 非会员只允许 MARD，若当前选中其他品牌则回退
  useEffect(() => {
    if (!availableBrands.find((b) => b.id === brandId)) {
      setBrandId(availableBrands[0]?.id || BRAND_OPTIONS[0].id)
    }
  }, [isPremium, availableBrands, brandId])

  // 切换品牌时同步默认单粒直径
  useEffect(() => {
    setBeadSize(getDefaultBeadSize(getBrandBaseId(brandId)))
  }, [brandId])

  // 切换品牌时，把已有 pattern 映射到新色库，避免预览变黑
  useEffect(() => {
    if (!pattern || !brand) return
    const remapped = remapPatternBrand(pattern, brand)
    if (remapped !== pattern) {
      setPattern(remapped)
    }
  }, [brand])

  // 记录已上传的 pattern 对象，避免同一图纸重复上传（PNG/PDF 各导一次）
  const uploadedPatternRef = useRef(null)

  // 生成完成后进入第 3 步（保存/分享改为导出时由用户选择）
  const handlePatternGenerated = async (p) => {
    uploadedPatternRef.current = null
    setPattern(p)
    setEditedPattern(null)
    setEditMode(false)
    setStep(3)
  }

  // 导出时按用户选择保存/分享到图集
  const maybeUploadToGallery = async () => {
    if (!user || !brand || !pattern) return
    if (saveMode === 'none') return
    if (uploadedPatternRef.current === pattern) return
    try {
      const dataUrl = renderPatternToDataURL(pattern, brand)
      const file = dataURItoFile(dataUrl, `doutian_${brand.id}_${pattern.width}x${pattern.height}.png`)
      await galleryApi.upload(file, {
        brand: brand.name,
        width: pattern.width,
        height: pattern.height,
        total: pattern.total,
        pattern: JSON.stringify({ grid: pattern.grid, legend: pattern.legend, width: pattern.width, height: pattern.height, total: pattern.total }),
        isPublic: saveMode === 'public',
        title: patternTitle.trim() || undefined,
      })
      uploadedPatternRef.current = pattern
    } catch (err) {
      console.error('upload to gallery failed', err)
      // 不再静默失败，提示用户保存未成功
      alert(`图纸已导出，但保存到图集失败：${err.response?.data?.error || err.message || '网络异常'}\n可重新导出一次或选择「不保存到图集」。`)
    }
  }

  // 渲染预览网格
  useEffect(() => {
    const activePattern = editedPattern || pattern
    if (!activePattern || !previewCanvasRef.current) return
    const canvas = previewCanvasRef.current
    const ctx = canvas.getContext('2d')

    // 熨烫效果预览：不改变图纸数据，仅换一种渲染方式
    if (previewEffect !== 'pattern') {
      const rendered = renderIroningPreview(activePattern, brand, CANVAS_SCALE, previewEffect)
      canvas.width = rendered.width
      canvas.height = rendered.height
      ctx.drawImage(rendered, 0, 0)
      return
    }

    const { grid, width, height } = activePattern
    canvas.width = width * CANVAS_SCALE
    canvas.height = height * CANVAS_SCALE

    const colorMap = new Map(brand.colors.map((c) => [c.id, c.hex]))
    const nameMap = new Map(brand.colors.map((c) => [c.id, c.name]))

    // 填充格子
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        ctx.fillStyle = colorMap.get(grid[y][x]) || '#000'
        ctx.fillRect(x * CANVAS_SCALE, y * CANVAS_SCALE, CANVAS_SCALE, CANVAS_SCALE)
      }
    }

    // 网格线（每 5×5 一组粗线）——深灰调，避免白色线条让图纸泛白
    if (showGrid) {
      const group = 5
      // 细网格
      ctx.strokeStyle = 'rgba(60, 50, 70, 0.18)'
      ctx.lineWidth = 1
      for (let x = 0; x <= width; x++) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y++) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
      // 粗线
      ctx.strokeStyle = 'rgba(60, 50, 70, 0.45)'
      ctx.lineWidth = 2
      for (let x = 0; x <= width; x += group) {
        ctx.beginPath()
        ctx.moveTo(x * CANVAS_SCALE, 0)
        ctx.lineTo(x * CANVAS_SCALE, height * CANVAS_SCALE)
        ctx.stroke()
      }
      for (let y = 0; y <= height; y += group) {
        ctx.beginPath()
        ctx.moveTo(0, y * CANVAS_SCALE)
        ctx.lineTo(width * CANVAS_SCALE, y * CANVAS_SCALE)
        ctx.stroke()
      }
    }

    // 色号文字
    if (showColorCodes && CANVAS_SCALE >= MIN_CELL_FOR_CODE) {
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // 与导出 PNG 的色号字号保持一致（瘦体加粗小号）
      ctx.font = `bold ${Math.max(6, Math.floor(CANVAS_SCALE * 0.24))}px 'Arial Narrow', 'PingFang SC', sans-serif`
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const id = grid[y][x]
          const hex = colorMap.get(id) || '#000'
          const code = nameMap.get(id) || ''
          const rgb = hexToRgb(hex)
          // 根据背景亮度决定文字颜色
          const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
          ctx.fillStyle = luminance > 0.5 ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)'
          ctx.fillText(code, x * CANVAS_SCALE + CANVAS_SCALE / 2, y * CANVAS_SCALE + CANVAS_SCALE / 2)
        }
      }
    }

    // 免费用户 subtle 水印
    if (!isPremium) {
      ctx.save()
      ctx.font = `${Math.max(14, width * 1.2)}px sans-serif`
      ctx.fillStyle = 'rgba(255,255,255,0.12)'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.translate(canvas.width / 2, canvas.height / 2)
      ctx.rotate(-Math.PI / 6)
      ctx.fillText('豆田造物', 0, 0)
      ctx.restore()
    }

    // 会员自定义水印（编辑模式预览）
    if (enableWatermark && userWatermark.trim()) {
      ctx.save()
      ctx.font = `${Math.max(10, width * 0.6)}px sans-serif`
      ctx.fillStyle = 'rgba(0,0,0,0.12)'
      ctx.textAlign = 'right'
      ctx.textBaseline = 'bottom'
      ctx.fillText(userWatermark.trim(), canvas.width - 8, canvas.height - 8)
      ctx.restore()
    }
  }, [pattern, editedPattern, brand, isPremium, showGrid, showColorCodes, enableWatermark, userWatermark, previewEffect])

  // 文件上传处理：先进入裁剪环节
  const [cropSrc, setCropSrc] = useState(null)
  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('请上传图片文件')
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => setCropSrc(e.target.result)
    reader.readAsDataURL(file)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files[0]
    handleFile(file)
  }

  // 生图等待期间小贴士轮播（每 4 秒换一条）
  useEffect(() => {
    if (!aiLoading) return
    const t = setInterval(() => setAiTipIndex((i) => (i + 1) % aiTips.length), 4000)
    return () => clearInterval(t)
  }, [aiLoading, aiTips])

  // AI 生图
  const generateAiImage = async () => {
    if (!aiPrompt.trim()) return
    setAiLoading(true)
    setAiTipIndex(0)
    setAiTask({ taskId: null, status: 'pending', imageUrl: null })
    try {
      const refSrcs = [...(aiStyleRef ? [aiStyleRef.src] : []), ...aiBaseImages.map((r) => r.src)]
      let referenceImageUrls = []
      if (refSrcs.length > 0) {
        setAiUploading(true)
        referenceImageUrls = await uploadReferenceImages(refSrcs)
        setAiUploading(false)
      }
      const { data } = await imageApi.generate(aiPrompt.trim(), aiStyle, 512, 512, referenceImageUrls, aiStyleRef ? 1 : 0)
      const taskId = data.taskId
      setAiTask({ taskId, status: data.status || 'pending', imageUrl: null })
      pollTask(taskId)
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'AI 生图失败')
      setAiLoading(false)
      setAiUploading(false)
      // 生成被拒（额度不足/过期）时同步最新额度，避免页面显示与服务端不一致
      refreshUsage()
    }
  }

  const uploadReferenceImages = async (srcs) => {
    const urls = []
    for (const src of srcs) {
      let file
      if (src.startsWith('data:')) {
        file = dataURItoFile(src, 'reference.png')
      } else {
        const res = await fetch(src, { credentials: 'same-origin' })
        if (!res.ok) throw new Error('无法读取参考图')
        const blob = await res.blob()
        file = new File([blob], 'reference.png', { type: blob.type || 'image/png' })
      }
      const { data } = await imageApi.upload(file)
      urls.push(data.url)
    }
    return urls
  }

  const addAiBaseImage = (src) => {
    if (aiBaseImages.length >= 4) return
    setAiBaseImages((prev) => [...prev, { id: crypto.randomUUID(), src }])
  }

  const removeAiBaseImage = (id) => {
    setAiBaseImages((prev) => prev.filter((r) => r.id !== id))
  }

  const handleAiStyleRefFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => setAiStyleRef({ id: crypto.randomUUID(), src: e.target.result })
    reader.readAsDataURL(file)
    if (aiStyleRefInputRef.current) aiStyleRefInputRef.current.value = ''
  }

  const handleAiBaseFiles = (files) => {
    if (!files) return
    const remaining = 4 - aiBaseImages.length
    const toAdd = Array.from(files).slice(0, remaining)
    toAdd.forEach((file) => {
      const reader = new FileReader()
      reader.onload = (e) => addAiBaseImage(e.target.result)
      reader.readAsDataURL(file)
    })
    if (aiBaseInputRef.current) aiBaseInputRef.current.value = ''
  }

  const useCurrentImageAsBase = () => {
    if (!image || aiBaseImages.length >= 4) return
    addAiBaseImage(image)
  }

  const pollTask = (taskId) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    pollIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await imageApi.status(taskId)
        setAiTask({ taskId, status: data.status, imageUrl: data.imageUrl })
        if (data.status === 'completed' || data.status === 'done' || data.imageUrl) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setAiLoading(false)
          // 生成成功已扣减额度，同步刷新保持与服务端一致
          refreshUsage()
          if (data.imageUrl) {
            setImage(data.imageUrl)
            setImageSource('ai')
            setStep(2)
          }
        } else if (data.status === 'failed' || data.status === 'error') {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
          setAiLoading(false)
          alert('图片生成失败')
        }
      } catch {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
        setAiLoading(false)
      }
    }, 2000)
  }

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }
  }, [])

  // 分享链接导入（小红书 / 抖音图文）
  const extractShareUrl = (text) => {
    const match = text.match(
      /(https?:\/\/(?:xhslink\.[a-z]+|www\.xiaohongshu\.com|m\.xiaohongshu\.com|xiaohongshu\.com|v\.douyin\.com|www\.douyin\.com|m\.douyin\.com|douyin\.com|iesdouyin\.com)\/[^\s'"<>]+)/i
    )
    return match ? match[1].replace(/[.,;!?。，；！？]$/, '') : ''
  }

  const doParseXhs = async () => {
    if (!xhsUrl.trim()) return
    const realUrl = extractShareUrl(xhsUrl) || xhsUrl.trim()
    if (!realUrl) {
      alert('未能从文本中提取到分享链接，请检查粘贴内容')
      return
    }
    setXhsLoading(true)
    setXhsImages([])
    try {
      const { data } = await xhsApi.parse(realUrl)
      setXhsTitle(data.title || '分享笔记')
      setXhsImages(data.images || [])
    } catch (err) {
      alert(err.response?.data?.error || err.message || '解析失败，请检查链接')
    } finally {
      setXhsLoading(false)
    }
  }

  const handleParseXhs = () => {
    if (!xhsUrl.trim()) return
    if (!storage.get('xhsDisclaimerAgreed')) {
      setShowXhsDisclaimer(true)
      return
    }
    doParseXhs()
  }

  const agreeXhsDisclaimer = () => {
    storage.set('xhsDisclaimerAgreed', '1')
    setShowXhsDisclaimer(false)
    doParseXhs()
  }

  const selectXhsImage = (url) => {
    setImage(xhsApi.proxyUrl(url))
    setImageSource('link')
    setXhsImages([])
    setStep(2)
  }

  // 导出文件名：优先使用用户填写的图纸名称
  const buildExportName = (ext) => {
    const title = patternTitle.trim().replace(/\s+/g, '_')
    const base = title ? `doutian_${title}` : `doutian_${brand.id}_${pattern.width}x${pattern.height}`
    return `${base}.${ext}`
  }

  // 导出 PNG：带页眉（用户名+品牌）和色号用量图例
  const exportPng = async () => {
    if (!pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'png', hd, width: pattern.width, height: pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const dataUrl = exportPatternPng({
      pattern: editedPattern || pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      beadSize,
      hd,
      watermarkText: enableWatermark ? userWatermark.trim() : null,
      mirrored: mirrorExport,
    })
    await downloadDataUrl(dataUrl, buildExportName('png'))
    refreshUsage()
    maybeUploadToGallery()
  }

  // 导出 PDF：带页眉（用户名+品牌）和色号用量图例
  const exportPdf = async () => {
    if (!pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'pdf', hd, width: pattern.width, height: pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const pdf = await exportPatternPdf({
      pattern: editedPattern || pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      maxWidth: limits.maxExportWidth,
      beadSize,
      hd,
      watermarkText: enableWatermark ? userWatermark.trim() : null,
      mirrored: mirrorExport,
    })
    // 统一走 blob 下载，兼容夸克/UC 等移动端浏览器
    downloadBlob(pdf.output('blob'), buildExportName('pdf'))
    refreshUsage()
    maybeUploadToGallery()
  }

  return (
    <div className="space-y-6">
      {/* 标题区 */}
      <section className="relative overflow-hidden rounded-[2rem] bg-creamy p-8 shadow-card md:p-10">
        <span className="pointer-events-none absolute -right-4 -top-4 text-8xl opacity-20">🧁</span>
        <h1 className="relative text-3xl font-extrabold tracking-tight text-cocoa md:text-4xl">
          深耕豆田，粒造万物 ✨
        </h1>
        <p className="relative mt-3 max-w-2xl text-cocoa-light">
          把喜欢的图片丢进豆田，我帮你变成超可爱的拼豆图纸~ 自动匹配品牌色库、计算用量，还能导出高清 PNG/PDF 哦！
        </p>
      </section>

      {/* 三步流程指引 */}
      <section className="card">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {[
            { step: 1, label: '选择原图', desc: '上传 / AI / 文字 / 链接' },
            { step: 2, label: '编辑调整', desc: '缩放移动 · 锁定参数' },
            { step: 3, label: '生成导出', desc: '参考线 · 色号 · 水印选项' },
          ].map((item, idx) => {
            const active = step === item.step
            const completed = step > item.step
            const reachable = item.step <= step
            return (
              <div key={item.step} className="flex flex-1 items-center">
                <button
                  type="button"
                  disabled={!reachable}
                  onClick={() => reachable && setStep(item.step)}
                  className={`flex-1 rounded-2xl py-2 px-2 text-center transition md:p-4 ${
                    active
                      ? 'bg-blush text-white shadow-soft'
                      : completed
                      ? 'bg-mint/30 text-cocoa'
                      : 'bg-cream-100 text-cocoa-light'
                  } ${reachable ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed opacity-60'}`}
                >
                  <div
                    className={`mx-auto mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold md:h-8 md:w-8 md:text-sm ${
                      active ? 'bg-white text-blush' : completed ? 'bg-mint text-white' : 'bg-lavender/30 text-cocoa-light'
                    }`}
                  >
                    {completed ? '✓' : item.step}
                  </div>
                  <p className="text-[11px] font-semibold md:text-sm">{item.label}</p>
                  <p className={`mt-0.5 hidden text-[10px] md:block ${active ? 'text-white/80' : 'text-cocoa-light'}`}>
                    {item.desc}
                  </p>
                </button>
                {idx < 2 && (
                  <div className="mx-1 h-0.5 w-4 bg-lavender/30 md:mx-2 md:w-8" />
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* 第 1 步：选择原图 */}
      {step === 1 && (
      <div className="space-y-6">
      {/* 草稿箱：检测到有未完成的编辑草稿 */}
      {draft?.pattern && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-peach/40 bg-peach-light/40 px-4 py-3">
          <span className="text-sm text-cocoa">
            📝 检测到未完成的图纸草稿（{draft.pattern.width}×{draft.pattern.height}，保存于 {new Date(draft.savedAt).toLocaleString('zh-CN')}）
          </span>
          <div className="flex gap-2">
            <button
              onClick={restoreDraft}
              className="rounded-full bg-blush px-4 py-1.5 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
            >
              恢复草稿
            </button>
            <button
              onClick={discardDraft}
              className="rounded-full border border-lavender/60 px-3 py-1.5 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
            >
              删除
            </button>
          </div>
        </div>
      )}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blush text-lg">🖼️</span>
          上传图片
        </h2>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`cursor-pointer rounded-3xl border-2 border-dashed p-6 text-center transition ${
            dragActive
              ? 'border-blush bg-blush-light/40'
              : 'border-blush/50 bg-white/80 hover:border-blush hover:bg-blush-light/20'
          } shadow-card`}
        >
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blush-light text-3xl shadow-soft">🖼️</div>
          <p className="text-sm font-semibold text-cocoa">把喜欢的图片丢进来~</p>
          <p className="mt-1 text-xs text-cocoa-light">支持 JPG / PNG / WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
        {!isPremium && (
          <p className="mt-2 text-xs text-cocoa-light">
            免费用户每天可导出 1 张低清带水印图纸，
            <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>
            解锁高清无水印、不限次导出~
          </p>
        )}

        {image && (
          <div className="mt-3 flex items-center gap-3 rounded-2xl border border-lavender/30 bg-white p-3">
            <img src={image} alt="preview" className="h-16 w-16 rounded-xl object-cover" />
            <div className="flex-1">
              <p className="text-sm font-medium text-cocoa">已加载图片</p>
              <p className="text-xs text-cocoa-light">在下方编辑区调整位置后点击「生成图纸」</p>
            </div>
            <button
              onClick={() => { setImage(null); setImageSource(null); setPattern(null) }}
              className="rounded-full px-3 py-1 text-xs text-cocoa-light hover:bg-cream-100"
            >
              移除
            </button>
          </div>
        )}
      </section>

      {/* 文字拼豆入口 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-peach text-lg">🔤</span>
          文字拼豆
        </h2>
        <p className="mb-3 text-sm text-cocoa-light">输入文字，一键生成像素字拼豆图纸，支持加边框。</p>
        <button
          onClick={() => setTextModalOpen(true)}
          className="btn-soft"
        >
          创建文字图纸
        </button>
      </section>

      {/* AI 生图入口 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-lavender text-lg">🎨</span>
          AI 生成参考图
        </h2>
        {!canUseAi ? (
          <div className="rounded-2xl bg-lavender/20 p-4 text-sm text-cocoa">
            <p>🌱 暂无 AI 生图额度~</p>
            <p className="mt-1 text-cocoa-light">
              去<a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>，
              或每日签到领取额度（首日 1 张，连续 7 天再送 3 张）。
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 md:flex-row">
              <input
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="例如：一只戴着草莓帽子的小猫咪 🍓"
                className="form-input flex-1"
                onKeyDown={(e) => e.key === 'Enter' && generateAiImage()}
              />
              <button
                onClick={generateAiImage}
                disabled={aiLoading || aiUploading || !aiPrompt.trim()}
                className={`btn-soft transition ${
                  aiLoading || aiUploading ? 'w-full animate-pulse-soft whitespace-normal text-left text-xs leading-relaxed' : 'shrink-0'
                }`}
              >
                {aiUploading
                  ? '上传垫图…'
                  : aiLoading
                  ? `⏳ 生成中 · ${aiTips[aiTipIndex % aiTips.length]}`
                  : isPremium
                  ? `生成参考图（剩余 ${user?.quota || 0}）`
                  : '生成参考图（今日免费 1 张）'}
              </button>
            </div>
            <div className="text-xs text-cocoa-light">
              {isPremium ? (
                <>
                  AI 额度剩余 {user?.quota || 0} 次
                  {quotaExpiresText ? ` · ${quotaExpiresText} 过期` : ''}
                </>
              ) : (
                <>
                  免费用户每日 1 张 AI 生图 ·{' '}
                  <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>{' '}
                  畅享更多
                </>
              )}
            </div>

            {/* 风格预设 */}
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { key: 'anime', label: '动漫像素风', img: '/styles/anime.png', badge: '人像' },
                { key: 'ghibli', label: '吉仆力像素风', img: '/styles/ghibli.png', badge: '治愈' },
                { key: 'stardew', label: '星露谷像素风', img: '/styles/stardew.png', badge: '田园' },
                { key: 'chibi', label: 'Q 版像素风', img: '/styles/qban.png', badge: '可爱' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setAiStyle(s.key)}
                  className={`overflow-hidden rounded-2xl border text-left transition ${
                    aiStyle === s.key
                      ? 'border-blush shadow-card ring-2 ring-blush/40'
                      : 'border-lavender/50 bg-white hover:border-blush'
                  }`}
                >
                  <img src={s.img} alt={s.label} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-xs font-semibold text-cocoa">{s.label}</span>
                    <span className="rounded-full bg-cocoa px-1.5 py-0.5 text-[10px] text-white">{s.badge}</span>
                  </div>
                </button>
              ))}
            </div>
            <button
              onClick={() => setAiStyle('')}
              className="mt-2 text-xs text-cocoa-light hover:text-cocoa"
            >
              不选择风格，使用默认提示词
            </button>

            {/* 参考图（风格，最多 1 张） */}
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-cocoa-light">参考图（风格参考，最多 1 张，选填）</span>
                {!aiStyleRef && (
                  <>
                    <button
                      onClick={() => aiStyleRefInputRef.current?.click()}
                      className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                    >
                      ➕ 上传参考图
                    </button>
                    <input
                      ref={aiStyleRefInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleAiStyleRefFile(e.target.files?.[0])}
                    />
                  </>
                )}
              </div>
              {aiStyleRef ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <div className="relative h-14 w-14 overflow-hidden rounded-xl border border-lavender/30">
                    <img src={aiStyleRef.src} alt="style-ref" className="h-full w-full object-cover" />
                    <button
                      onClick={() => setAiStyleRef(null)}
                      className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cocoa text-[10px] text-white shadow-sm"
                      title="移除"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-xs text-cocoa-light">未选参考图时，以手动选择的风格卡或默认风格生成</p>
              )}
            </div>

            {/* 垫图（内容主体，最多 4 张） */}
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-cocoa-light">垫图（内容主体，最多 4 张，选填）</span>
                {aiBaseImages.length < 4 && (
                  <>
                    <button
                      onClick={() => aiBaseInputRef.current?.click()}
                      className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                    >
                      ➕ 上传垫图
                    </button>
                    <input
                      ref={aiBaseInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handleAiBaseFiles(e.target.files)}
                    />
                    {image && (
                      <button
                        onClick={useCurrentImageAsBase}
                        className="rounded-full border border-lavender/60 px-2.5 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                      >
                        使用当前原图
                      </button>
                    )}
                  </>
                )}
              </div>

              {aiBaseImages.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {aiBaseImages.map((ref) => (
                    <div key={ref.id} className="relative h-14 w-14 overflow-hidden rounded-xl border border-lavender/30">
                      <img src={ref.src} alt="base" className="h-full w-full object-cover" />
                      <button
                        onClick={() => removeAiBaseImage(ref.id)}
                        className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cocoa text-[10px] text-white shadow-sm"
                        title="移除"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-xs text-cocoa-light">人物 / 宠物 / 文字 / 物品均可，未选垫图时按文字描述创作</p>
              )}
            </div>

            {aiTask && (
              <div className="mt-3 text-sm text-cocoa-light">
                任务状态：<span className="font-medium text-blush-dark">{aiTask.status}</span>
                {aiTask.imageUrl && '（已自动加载到生成器）'}
              </div>
            )}
          </>
        )}
      </section>

      {/* 小红书导入 */}
      <section className="card">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-cocoa">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-mint text-lg">📕</span>
          小红书 / 抖音链接导入
        </h2>
        <div className="flex flex-col gap-3 md:flex-row">
          <input
            value={xhsUrl}
            onChange={(e) => setXhsUrl(e.target.value)}
            placeholder="粘贴小红书或抖音分享链接，例如 http://xhslink.cn/... 或 http://v.douyin.com/..."
            className="form-input flex-1"
            onKeyDown={(e) => e.key === 'Enter' && handleParseXhs()}
          />
          <button
            onClick={handleParseXhs}
            disabled={xhsLoading || !xhsUrl.trim()}
            className="btn-soft shrink-0"
          >
            {xhsLoading ? '解析中…' : '解析笔记'}
          </button>
        </div>
        <p className="mt-2 text-xs text-cocoa-light">
          解析他人笔记前请征得原作者同意，禁止商用或侵犯版权。
        </p>

        {showXhsDisclaimer && (
          <div className="mt-3 rounded-2xl border border-lavender/40 bg-cream-50/60 p-4">
            <h3 className="text-sm font-bold text-cocoa">分享链接导入免责声明</h3>
            <div className="mt-2 space-y-1 text-xs text-cocoa-light">
              <p>本功能仅作为个人学习、交流便利工具，解析前请确保已征得原作者同意。</p>
              <p>禁止将解析内容用于商业用途、二次分发或任何侵犯原作者版权的行为。</p>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => setShowXhsDisclaimer(false)}
                className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
              >
                取消
              </button>
              <button
                onClick={agreeXhsDisclaimer}
                className="rounded-full bg-blush px-3 py-1 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
              >
                我已阅读并同意
              </button>
            </div>
          </div>
        )}

        {xhsImages.length > 0 && (
          <div className="mt-4 rounded-2xl bg-cream-50/60 p-4">
            <p className="mb-3 text-sm font-semibold text-cocoa">
              识别到 {xhsImages.length} 张图片：{xhsTitle}
            </p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
              {xhsImages.map((url, idx) => (
                <button
                  key={idx}
                  onClick={() => selectXhsImage(url)}
                  className="relative aspect-square overflow-hidden rounded-xl border border-lavender/30 bg-white hover:ring-2 hover:ring-blush"
                >
                  <img
                    src={xhsApi.proxyUrl(url)}
                    alt={`xhs-${idx}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-cocoa-light">点击图片即可加载到生成器~</p>
          </div>
        )}
      </section>
      </div>
      )}

      {/* 第 2 步：编辑调整 */}
      {step === 2 && (
      <div className="grid gap-6 lg:grid-cols-3">
        {/* 左侧控制面板 */}
        <section className="space-y-4 lg:col-span-1">
          {/* 参数面板 */}
          <div className="card space-y-4">
            <div>
              <label className="mb-1 flex items-center justify-between text-sm font-medium text-cocoa">
                <span>拼豆品牌 / 色库</span>
                {!isPremium && (
                  <a href="/pricing" className="text-xs text-blush-dark hover:underline">
                    升级解锁全部品牌
                  </a>
                )}
              </label>
              <select
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className="form-input"
              >
                {availableBrands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {!isPremium && (
                <p className="mt-1 text-[10px] text-cocoa-light">
                  免费用户仅可使用 MARD 色库
                </p>
              )}
            </div>

            {/* 画板尺寸滑块已移入「图片编辑」卡片（编辑画板上方） */}

            {/* 图像优化强度：AI 高还原模式下跳过优化，不显示 */}
            {!faithful && (
              <div>
                <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                  <span>图像优化强度</span>
                  <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                    {OPTIMIZE_LABEL}
                  </span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={optimizeValue}
                  onChange={(e) => setOptimizeValue(Number(e.target.value))}
                  className="w-full accent-blush"
                />
                <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                  <span>弱</span>
                  <span>标准</span>
                  <span>强</span>
                </div>
              </div>
            )}

            {/* 最大颜色数 */}
            <div>
              <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                <span>最大颜色数</span>
                <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                  {maxColors} 色
                </span>
              </label>
              <div className="grid grid-cols-5 gap-2">
                {[12, 20, 36, 64].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => {
                      setMaxColors(v)
                      setIsCustomColorCount(false)
                    }}
                    className={`rounded-lg border py-1.5 text-xs font-semibold transition ${
                      maxColors === v && !isCustomColorCount
                        ? 'border-blush bg-blush text-white'
                        : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                    }`}
                  >
                    {v}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setIsCustomColorCount(true)}
                  className={`rounded-lg border py-1.5 text-xs font-semibold transition ${
                    isCustomColorCount
                      ? 'border-blush bg-blush text-white'
                      : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                  }`}
                >
                  自定义
                </button>
              </div>
              {isCustomColorCount && (
                <div className="mt-2">
                  <input
                    type="range"
                    min={2}
                    max={150}
                    step={1}
                    value={maxColors}
                    onChange={(e) => setMaxColors(Number(e.target.value))}
                    className="w-full accent-blush"
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                    <span>2 色</span>
                    <span className="font-medium text-blush-dark">当前 {maxColors} 色</span>
                    <span>150 色</span>
                  </div>
                </div>
              )}
            </div>

            {/* 高级选项 */}
            <div className="rounded-2xl border border-lavender/30 bg-cream-50/60">
              <button
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-cocoa"
              >
                <span className="flex items-center gap-2">🌾 高级选项</span>
                <span className="text-cocoa-light transition-transform">{showAdvanced ? '▲' : '▼'}</span>
              </button>
              {showAdvanced && (
                <div className="space-y-4 px-4 pb-4">
                  {faithful ? (
                    <p className="rounded-xl border border-blush/30 bg-blush-light/20 px-3 py-2 text-xs leading-relaxed text-cocoa">
                      ✨ 当前为 AI 生图，已启用<b>高还原模式</b>：最大化保留原图色彩与边界，不做描边、合并与杂色清理。
                    </p>
                  ) : (
                    <>
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                        <span className="text-sm text-cocoa">自动描边（仅勾勒主体外轮廓）</span>
                        <input
                          type="checkbox"
                          checked={outline}
                          onChange={(e) => setOutline(e.target.checked)}
                          className="h-4 w-4 accent-blush"
                        />
                      </label>
                      <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                        <span className="text-sm text-cocoa">柔和过渡（抖动）</span>
                        <input
                          type="checkbox"
                          checked={dithering}
                          onChange={(e) => setDithering(e.target.checked)}
                          className="h-4 w-4 accent-blush"
                        />
                      </label>
                    </>
                  )}

                  {/* 单粒直径 */}
                  <div>
                    <label className="mb-1 flex items-center justify-between text-xs font-medium text-cocoa-light">
                      <span>单粒直径</span>
                      <span className="rounded-full bg-blush px-2 py-0.5 text-[10px] text-white">{beadSize} mm</span>
                    </label>
                    <div className="flex gap-2">
                      {[2.6, 5, 10].map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={paramsLocked}
                          onClick={() => setBeadSize(s)}
                          className={`flex-1 rounded-lg border py-1 text-xs transition disabled:opacity-50 ${
                            beadSize === s
                              ? 'border-blush bg-blush text-white'
                              : 'border-lavender/40 bg-white text-cocoa hover:border-blush'
                          }`}
                        >
                          {s} mm
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* 显示网格 */}
                  <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                    <span className="text-sm text-cocoa">显示网格</span>
                    <input
                      type="checkbox"
                      checked={showGrid}
                      onChange={(e) => setShowGrid(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                  </label>

                  {/* 显示 5×5 辅助线 */}
                  <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                    <span className="text-sm text-cocoa">显示 5×5 辅助线</span>
                    <input
                      type="checkbox"
                      checked={showGroupLines}
                      onChange={(e) => setShowGroupLines(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                  </label>
                </div>
              )}
            </div>

            {!isPremium && (
              <p className="text-xs leading-relaxed text-cocoa-light">
                小萌新最大支持 {limits.maxBoardSize}×{limits.maxBoardSize}，导出带水印。
                <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级田主会员</a> 解锁高清无水印~
              </p>
            )}
          </div>

        </section>

        {/* 右侧预览区 -- 图片编辑与生成结果 */}
        <section className="space-y-4 lg:col-span-2">
          {image && (
            <div className="card">
              <h3 className="mb-1 text-lg font-bold text-cocoa">🖼️ 图片编辑</h3>
              <p className="mb-3 text-xs text-cocoa-light">
                拖拽移动 · 双指捏合缩放 · 滚轮缩放 · 网格 {paramsLocked && liveGridSize ? liveGridSize : isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}×{paramsLocked && liveGridSize ? liveGridSize : isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}
                {paramsLocked && liveGridSize && <span className="ml-1 text-blush-dark">（锁定后实时）</span>}
                {faithful && <span className="ml-2 rounded-full bg-blush-light/40 px-2 py-0.5 text-blush-dark">✨ AI 高还原模式</span>}
              </p>
              <BeadEditor
                ref={beadEditorRef}
                src={image}
                gridSize={isPremium ? boardSize : Math.min(boardSize, limits.maxBoardSize)}
                brand={brand}
                maxColors={maxColors}
                dithering={effDithering}
                mergeThreshold={effMerge}
                denoise={effDenoise}
                pixelMode={effPixelMode}
                outline={effOutline}
                faithful={faithful}
                saturation={effSaturation}
                preprocessStrength={effPreprocessStrength}
                locked={paramsLocked}
                onGenerate={handlePatternGenerated}
                onGridSizeChange={(size) => setBoardSize(size)}
                onCellEditChange={(editing) => setIsCellEditing(editing)}
                onLiveGridSizeChange={(n) => {
                  setLiveGridSize(n)
                  // 锁定后缩放实时反推画幅规格，同步到上方滑动框
                  if (n) setBoardSize(n)
                }}
                headerControl={
                  <div className="mb-3">
                    <label className="mb-2 flex items-center justify-between text-sm font-medium text-cocoa">
                      <span>画板尺寸</span>
                      <span className="rounded-full bg-blush px-2 py-0.5 text-xs text-white">
                        {boardSize} × {boardSize}（约 {gridToCm(boardSize, beadSize)} × {gridToCm(boardSize, beadSize)} cm）
                      </span>
                    </label>
                    <input
                      type="range"
                      min={10}
                      max={Math.max(limits.maxBoardSize, boardSize)}
                      step={1}
                      value={boardSize}
                      disabled={paramsLocked}
                      onChange={(e) => setBoardSize(Number(e.target.value))}
                      className="w-full accent-blush disabled:opacity-50"
                    />
                    <div className="mt-1 flex justify-between text-[10px] text-cocoa-light">
                      <span>10 × 10</span>
                      <span>{Math.max(limits.maxBoardSize, boardSize)} × {Math.max(limits.maxBoardSize, boardSize)}</span>
                    </div>
                  </div>
                }
                toolbarControl={
                  <button
                    type="button"
                    onClick={() => {
                      if (isCellEditing) {
                        // 单粒编辑模式下，点击锁定按钮 = 应用
                        beadEditorRef.current?.applyCellEdit()
                        setParamsLocked(true)
                      } else {
                        setParamsLocked((v) => {
                          if (v) setLiveGridSize(null)
                          return !v
                        })
                      }
                    }}
                    className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                      paramsLocked
                        ? 'border border-lavender/60 bg-cream-200 text-cocoa-muted hover:bg-cream-100'
                        : 'bg-blush text-white shadow-soft hover:bg-blush-dark'
                    }`}
                  >
                    {paramsLocked ? '🔒 已锁定（点击解锁）' : '🔓 锁定画板与豆子尺寸'}
                  </button>
                }
              />
            </div>
          )}

          {step === 2 && !pattern && (
            <div className="flex h-96 flex-col items-center justify-center rounded-[2rem] border-2 border-dashed border-lavender/40 bg-white text-center shadow-card">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blush-light text-5xl shadow-soft">🎀</div>
              <p className="mt-5 text-cocoa-light">调整位置后，点击上方「生成图纸」进入下一步~</p>
            </div>
          )}
        </section>
      </div>
      )}

      {/* 第 3 步：生成导出 */}
      {step === 3 && pattern && (
        <section className="card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-lg font-bold text-cocoa">
                {editMode ? '✏️ 编辑图纸' : '生成结果'}
              </h3>
              <p className="text-sm text-cocoa-light">
                {brand.name} · {pattern.width}×{pattern.height} · 共 {(editedPattern || pattern).total} 豆 · 约 {gridToCm(pattern.width, beadSize)} × {gridToCm(pattern.height, beadSize)} cm
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!editMode && (
                <button
                  onClick={() => {
                    setEditedPattern(structuredClone(pattern))
                    setSelectedColorId(brand.colors[0]?.id || null)
                    setPickColorMode(false)
                    setEditHistory([])
                    setPreviewEffect('pattern') // 编辑只在图纸模式下进行
                    setEditMode(true)
                  }}
                  className="rounded-full border border-lavender/60 bg-white px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                >
                  ✏️ 编辑图纸
                </button>
              )}
              <div className="text-sm text-cocoa-light">
                颜色种类：<span className="font-medium text-blush-dark">{(editedPattern || pattern).legend.length}</span>
              </div>
            </div>
          </div>

          {/* 编辑模式：颜色选择器 */}
          {editMode && (
            <div className="rounded-2xl border border-lavender/30 bg-cream-50/60 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-cocoa">
                  {pickColorMode ? '点击图纸任意位置吸附颜色' : '选择颜色后点击图纸修改'}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={undoEdit}
                    disabled={editHistory.length === 0}
                    className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30 disabled:opacity-50"
                  >
                    ↩️ 撤回
                  </button>
                  <button
                    onClick={() => {
                      setPickColorMode(false)
                      setEditHistory([])
                      discardDraft()
                      setEditMode(false)
                    }}
                    className="rounded-full border border-lavender/60 px-3 py-1 text-xs font-medium text-cocoa transition hover:bg-lavender/30"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => {
                      if (editedPattern) {
                        setPattern(editedPattern)
                      }
                      setPickColorMode(false)
                      setEditHistory([])
                      discardDraft()
                      setEditMode(false)
                    }}
                    className="rounded-full bg-blush px-3 py-1 text-xs font-semibold text-white shadow-soft transition hover:bg-blush-dark"
                  >
                    完成编辑
                  </button>
                </div>
              </div>

              {/* 取色吸附 + 当前颜色指示 */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPickColorMode((v) => !v)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    pickColorMode
                      ? 'border-blush bg-blush text-white shadow-soft'
                      : 'border-lavender/60 bg-white text-cocoa hover:bg-lavender/30'
                  }`}
                >
                  {pickColorMode ? '💧 取色中…点击图纸吸附' : '💧 吸附图纸颜色'}
                </button>
                {selectedColorId && (
                  <span className="flex items-center gap-1.5 text-xs text-cocoa-light">
                    当前颜色
                    <span
                      className="h-5 w-5 rounded-md border border-black/10 shadow-sm"
                      style={{ backgroundColor: brand.colors.find((c) => c.id === selectedColorId)?.hex || '#fff' }}
                    />
                    <span className="font-medium text-cocoa">
                      {brand.colors.find((c) => c.id === selectedColorId)?.name || selectedColorId}
                    </span>
                  </span>
                )}
                <span className="text-[10px] text-cocoa-light">编辑自动保存草稿 · 可单步撤回</span>
              </div>

              {/* 品牌色板（含色号） */}
              <div>
                <p className="mb-1.5 text-xs text-cocoa-light">{brand.name} 色板</p>
                <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                  {brand.colors.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedColorId(c.id)}
                      className={`flex w-9 flex-col items-center rounded-md border-2 p-0.5 transition ${
                        selectedColorId === c.id
                          ? 'border-blush bg-blush-light/30 shadow-sm'
                          : 'border-transparent hover:bg-lavender/20'
                      }`}
                      title={c.name}
                    >
                      <span
                        className="h-6 w-full rounded-sm border border-black/10"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span className="mt-0.5 w-full truncate text-center text-[9px] leading-tight text-cocoa-light">
                        {c.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 会员水印 */}
              {isPremium ? (
                <div className="flex items-center gap-3 rounded-xl border border-lavender/30 bg-white px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enableWatermark}
                      onChange={(e) => setEnableWatermark(e.target.checked)}
                      className="h-4 w-4 accent-blush"
                    />
                    <span className="text-sm text-cocoa">添加水印</span>
                  </label>
                  {enableWatermark && (
                    <input
                      type="text"
                      value={userWatermark}
                      onChange={(e) => setUserWatermark(e.target.value.slice(0, 20))}
                      placeholder="输入水印文字"
                      className="form-input flex-1 text-sm py-1"
                    />
                  )}
                </div>
              ) : (
                <p className="text-xs text-cocoa-light">
                  💡 <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a> 可添加自定义水印
                </p>
              )}
            </div>
          )}

          {/* 预览效果切换：图纸 / 熨烫效果 */}
          {!editMode && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-cocoa-light">预览效果</span>
              {IRONING_EFFECTS.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  onClick={() => setPreviewEffect(e.key)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    previewEffect === e.key
                      ? 'border-blush bg-blush text-white shadow-soft'
                      : 'border-lavender/60 bg-white text-cocoa hover:bg-lavender/30'
                  }`}
                >
                  {e.label}
                </button>
              ))}
              {previewEffect !== 'pattern' && (
                <span className="text-[10px] text-cocoa-light">
                  熨烫效果为模拟预览，实际以熨烫后为准
                </span>
              )}
            </div>
          )}

          <div className="overflow-auto rounded-2xl border border-cream-200 bg-cream-100 p-2">
            <canvas
              ref={previewCanvasRef}
              onClick={(e) => {
                if (!editMode || !editedPattern || !selectedColorId || previewEffect !== 'pattern') return
                const canvas = previewCanvasRef.current
                if (!canvas) return
                const rect = canvas.getBoundingClientRect()
                const scaleX = canvas.width / rect.width
                const scaleY = canvas.height / rect.height
                const x = Math.floor(((e.clientX - rect.left) * scaleX) / CANVAS_SCALE)
                const y = Math.floor(((e.clientY - rect.top) * scaleY) / CANVAS_SCALE)
                const w = editedPattern.width
                const h = editedPattern.height
                if (x < 0 || x >= w || y < 0 || y >= h) return

                // 取色模式：吸附该格颜色为当前画笔色，并自动切回涂色模式
                if (pickColorMode) {
                  setSelectedColorId(editedPattern.grid[y][x])
                  setPickColorMode(false)
                  return
                }

                // 记录历史用于单步撤回（最多 50 步）
                setEditHistory((prev) => [...prev.slice(-49), editedPattern.grid])

                const newGrid = editedPattern.grid.map((row) => [...row])
                newGrid[y][x] = selectedColorId

                const { legend, total } = recomputePatternStats(newGrid)
                setEditedPattern({
                  ...editedPattern,
                  grid: newGrid,
                  legend,
                  total,
                })
              }}
              className={`mx-auto block max-w-full h-auto rounded-xl shadow-sm ${
                editMode ? 'cursor-crosshair' : ''
              }`}
              style={{ imageRendering: 'pixelated' }}
            />
          </div>

          {/* 导出按钮 */}
          {!editMode && (
            <div className="space-y-3 rounded-2xl border border-lavender/30 bg-cream-50/60 p-3">
              <p className="text-xs text-cocoa-light">
                {isPremium ? '本月已导出' : '今日已导出'} {user?.exportsUsed || 0} / {user?.exportsLimit || 1} 次
                {!isPremium && (
                  <>
                    {' · '}
                    <a href="/pricing" className="font-semibold text-blush-dark hover:underline">升级会员</a>
                    不限次高清无水印
                  </>
                )}
              </p>

              {/* 图纸名称（选填，用作导出文件名与图集标题） */}
              <div>
                <input
                  type="text"
                  value={patternTitle}
                  onChange={(e) => {
                    // 实时过滤：仅保留中英文、数字和空格
                    const filtered = e.target.value.replace(/[^\u4e00-\u9fa5a-zA-Z0-9\s]/g, '')
                    setPatternTitle(filtered.slice(0, 30))
                  }}
                  maxLength={30}
                  placeholder="图纸名称（选填，用作文件名），例如：草莓小猫"
                  className="form-input text-sm"
                />
              </div>

              {/* 保存/分享选择 */}
              {user && (
                <select
                  value={saveMode}
                  onChange={(e) => setSaveMode(e.target.value)}
                  className="form-input text-sm"
                >
                  <option value="none">不保存到图集</option>
                  <option value="private">保存到图集（仅自己可见）</option>
                  <option value="public">保存到图集并公开分享</option>
                </select>
              )}

              {/* 导出选项：显示色号（默认勾选） */}
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                <span className="text-sm text-cocoa">显示色号（导出图纸标注每粒色号）</span>
                <input
                  type="checkbox"
                  checked={showColorCodes}
                  onChange={(e) => setShowColorCodes(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
              </label>

              {/* 镜像导出开关 */}
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-lavender/40 bg-white px-3 py-2">
                <span className="text-sm text-cocoa">镜像导出（图纸左右翻转）</span>
                <input
                  type="checkbox"
                  checked={mirrorExport}
                  onChange={(e) => setMirrorExport(e.target.checked)}
                  className="h-4 w-4 accent-blush"
                />
              </label>

              <div className="flex gap-2">
                <button onClick={exportPng} className="btn-primary flex-1 py-2 text-sm md:py-2.5 md:text-base">
                  导出 PNG 🖼️
                </button>
                <button onClick={exportPdf} className="btn-soft flex-1 py-2 text-sm md:py-2.5 md:text-base">
                  导出 PDF 📄
                </button>
              </div>
            </div>
          )}

          {/* 图例 & 用量 */}
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cocoa-light">色号图例 & 用量</h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
              {[...(editedPattern || pattern).legend]
                .sort((a, b) => b.count - a.count)
                .map((item) => {
                  const total = (editedPattern || pattern).total
                  const percent = total > 0 ? ((item.count / total) * 100).toFixed(1) : '0.0'
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 rounded-2xl border border-lavender/30 bg-white p-2 shadow-sm"
                    >
                      <div
                        className="h-8 w-8 rounded-xl border border-black/10 shadow-sm"
                        style={{ backgroundColor: item.hex }}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-cocoa">{item.name}</p>
                        <p className="text-xs text-cocoa-light">× {item.count} · {percent}%</p>
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </section>
      )}

      <TextBeadModal
        open={textModalOpen}
        brand={brand}
        onClose={() => setTextModalOpen(false)}
        onConfirm={handlePatternGenerated}
      />

      <ImageCropModal
        open={!!cropSrc}
        src={cropSrc}
        onCancel={() => setCropSrc(null)}
        onConfirm={(dataUrl) => {
          setCropSrc(null)
          setImage(dataUrl)
          setImageSource('upload')
          setStep(2)
        }}
      />
    </div>
  )
}
